/**
 * Consistency-scorecard tolerances — how forgiving each zone-detail row is.
 * All rows compare laps to the MEDIAN; a lap outside median ± tolerance is
 * "off" for that row. See consistencyThresholds.ts / consistencyPercent.ts
 * for the derivation functions that combine these.
 */

/** Unit: km/h. Default: 5. Floor for the speed tolerance; also the flat
 * per-bin gate for the speed-curve row. RAISE to forgive more speed spread. */
export const SPEED_OK_THRESHOLD_KMH = 5
/** Unit: %. Default: 3. Speed tolerance = this % of the row median, floored
 * at SPEED_OK_THRESHOLD_KMH (200 km/h → ±6, 250 km/h → ±8). */
export const SPEED_TOLERANCE_PCT = 3
/** Unit: metres. Default: 5. Floor for distance/brake-point tolerance. */
export const BRAKE_OK_THRESHOLD_M = 5
/** Unit: ms of travel at the row's reference speed. Default: 100. Distance
 * tolerance = track covered in this window, floored at BRAKE_OK_THRESHOLD_M
 * (±5 m equals 0.1 s at 180 km/h). RAISE to forgive later/earlier braking. */
export const DISTANCE_TOLERANCE_REACTION_MS = 100
/** Unit: laps. Default: 3. Below this, no consistency % is computed. */
export const MIN_LAPS_FOR_CONSISTENCY = 3
/** Unit: ms. Default: 100. Floor for the zone time-row tolerance. */
export const TIME_OK_THRESHOLD_MS = 100
/** Unit: ms per metre of zone length. Default: 1.5. The time-row tolerance
 * scales with zone length (100 m → 0.15 s, 200 m → 0.3 s) — longer zones
 * accumulate proportionally more natural lap-time spread. LOWER this if the
 * time tolerance feels too generous on long zones (owner watch-item). */
export const TIME_TOLERANCE_MS_PER_METER = 1.5
/** Unit: ms. Default: 50. Time tolerance rounds to this step so the defense
 * label stays honest. */
export const TIME_TOLERANCE_STEP_MS = 50
/** Unit: raw %. Default: 80. A row/zone counts as "consistent" at/above
 * this; also gates culprit attribution and one-off vs habit classification. */
export const CONSISTENT_ROW_MIN_PCT = 80
/** Unit: 5 m bins. Default: 3. Minimum valid bins for the speed-curve row. */
export const MIN_CURVE_BINS = 3
/** Unitless multiplier. Default: 3. A value must sit more than K × the
 * rest-spread (plus the metric's floor) from the rest to be NAMED the single
 * outlier lap. LOWER to name culprits more eagerly; RAISE to be stricter. */
export const OUTLIER_K = 3
/** Unit: km/h. Default: 10 (2 × SPEED_OK_THRESHOLD_KMH). Floor for the
 * anomaly ("likely traffic") magnitude gate — a lap must run at least this
 * much slower than the rest before it can be set aside. RAISE to set aside
 * fewer laps. */
export const ANOMALY_MAGNITUDE_FLOOR_KMH = 10
/** Unitless fraction. Default: 0.5. A candidate's slow stretch must be a
 * CONTIGUOUS run covering at least this fraction of the zone's valid bins.
 * RAISE to demand more of the zone be affected. */
export const ANOMALY_COVERAGE_FRACTION = 0.5
/** Unitless fraction. Default: 0.25. Cap on the share of laps set aside per
 * zone; more candidates than this → the zone is 'traffic-disrupted' and not
 * scored. LOWER to give up on scoring sooner. */
export const ANOMALY_MAX_SHARE = 0.25
